import { useEffect, useMemo, useState, type CSSProperties } from 'react'
import { supabase } from '../lib/supabase'

interface IdentityImageProps {
  path: string | null | undefined
  version?: string | null
  name: string
  kind: 'business' | 'client'
  size?: number
  alt?: string
  className?: string
}

interface CachedMediaUrl {
  url: string
  expiresAt: number
}

const signedMediaCache = new Map<string, CachedMediaUrl>()

export function clearSignedAccountMedia(path: string) {
  for (const key of signedMediaCache.keys()) {
    if (key.startsWith(`${path}#`)) signedMediaCache.delete(key)
  }
}

export function IdentityImage({
  path,
  version,
  name,
  kind,
  size = 44,
  alt,
  className = '',
}: IdentityImageProps) {
  const [url, setUrl] = useState<string | null>(null)
  const cacheKey = path ? `${path}#${version ?? ''}` : null
  const initials = useMemo(() => makeInitials(name), [name])

  useEffect(() => {
    let active = true
    if (!path || !cacheKey) {
      setUrl(null)
      return () => { active = false }
    }

    const cached = signedMediaCache.get(cacheKey)
    if (cached && cached.expiresAt > Date.now() + 60_000) {
      setUrl(cached.url)
      return () => { active = false }
    }

    setUrl(null)
    void supabase.storage.from('account-media').createSignedUrl(path, 3_600).then(({ data, error }) => {
      if (!active || error || !data?.signedUrl) return
      const separator = data.signedUrl.includes('?') ? '&' : '?'
      const signedUrl = `${data.signedUrl}${separator}v=${encodeURIComponent(version ?? String(Date.now()))}`
      signedMediaCache.set(cacheKey, { url: signedUrl, expiresAt: Date.now() + 3_600_000 })
      setUrl(signedUrl)
    })

    return () => { active = false }
  }, [cacheKey, path, version])

  const style = { '--identity-image-size': `${size}px` } as CSSProperties
  const label = alt ?? (kind === 'business' ? `${name} logo` : `${name} profile photo`)

  return (
    <span
      className={`identity-image identity-image--${kind} ${className}`.trim()}
      style={style}
      role="img"
      aria-label={label}
    >
      {url
        ? <img src={url} alt="" onError={() => setUrl(null)} />
        : <span aria-hidden="true">{initials}</span>}
    </span>
  )
}

function makeInitials(name: string) {
  const initials = name.trim().split(/\s+/).slice(0, 2).map((part) => part[0] ?? '').join('')
  return initials.toUpperCase() || 'T'
}
